import { noop } from 'lodash';
import { createContext, useCallback, useEffect, useState } from 'react';
import { CenteredContainer } from '.';
import WorkerApi from '../worker';
import ErrorLayout from './ErrorLayout';
import Loading from './Loading';


type QueryLoaderType<TValue, TVars> = {
    query: string,
    variables: TVars,
    render: (data: TValue, reload: () => void) => JSX.Element,
    reloadInterval?: number,
    onError?: ((error: any, retry: () => void) => JSX.Element),
    onLoading?: ((progress: number|undefined, abort: () => void) => JSX.Element)
}

type QueryLoaderContextObject = {
    reload: () => void,
    loading: boolean
};

export const QueryLoaderContext = createContext<QueryLoaderContextObject>({
    reload: noop,
    loading: false
});

const QueryLoader = <TValue, TVars = undefined>({ query, variables, reloadInterval, render, onError, onLoading }: QueryLoaderType<TValue, TVars>): JSX.Element => {
    const [data, setData] = useState<TValue | null>(null);
    const [load, setLoad] = useState<boolean>(true);
    const [error, setError] = useState<JSX.Element | string | null>(null);

    useEffect(() => {
        if (load === true) {

            let abort = false;
            //let timeoutHandle:number|null = null;

            setError(null);

            WorkerApi.query<TValue, TVars>({ query, variables }).then((data) => {
                if (abort === false) {
                    setData(data);
                    setLoad(false);
                }
            }).catch((e) => {
                if (abort === false) {
                    setError(e.toString());
                    setLoad(false);
                }
            });

            return (() => {
                abort = true;
            });
        }
    }, [load, query, variables])

    useEffect(() => {
        if (load === false && error === null && reloadInterval !== undefined) {
            let timeoutHandle = setTimeout(setLoad, reloadInterval, true);
            return (() => clearTimeout(timeoutHandle));
        }
    }, [load, error, reloadInterval]);

    const reload = useCallback(() => setLoad(true), [setLoad]);
    const abort = useCallback(() => setLoad(false), [setLoad]);

    if (error !== null) {
        return onError === undefined?<ErrorLayout>{error}</ErrorLayout>:onError(error, reload);
    }

    if (data === null) {
        return onLoading === undefined ? <CenteredContainer><Loading/></CenteredContainer> : onLoading(undefined, abort);
    }

    return <QueryLoaderContext.Provider value={{ reload, loading: load }}>{render(data as TValue, reload)}</QueryLoaderContext.Provider>;
}

export default QueryLoader;
